import { FC, useRef, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { Button } from '@kontentino/ui';
import { ApiClientError } from 'api/client';
import { queryKey } from 'constants/queryKey';
import Modal from 'components/shared/Modal';
import ConfirmDialog from 'app/components/ConfirmDialog';
import useSubscriptionInfo from 'app/hooks/useSubscriptionInfo';
import { useToast } from 'app/hooks/useToast';
import { getErrorCode, getErrorStatus } from 'app/utils/apiError';
import { formatDate } from 'app/utils/date';
import { AccountSwitcherApi } from '../api/accountSwitcherApi';
import { useConnectedAccountsQuery } from '../hooks/useConnectedAccountsQuery';
import { useSwitchAccountMutation } from '../hooks/useSwitchAccountMutation';
import usePendingInvitationStore from '../hooks/usePendingInvitationStore';
import {
  GetConnectedAccountsResponse,
  PendingInvitation,
} from '../types/accountSwitcher';

const INVITATION_NOT_PENDING = 'INVITATION_NOT_PENDING';
const TOO_MANY_REQUESTS = 429;

const PendingInvitationModal: FC = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { isTrialExpired, isSubscriptionExpired } = useSubscriptionInfo();
  const { data } = useConnectedAccountsQuery();
  const switchAccount = useSwitchAccountMutation();
  const { openAccountId, dismissed, close } = usePendingInvitationStore();
  const [isDeclineConfirmOpen, setIsDeclineConfirmOpen] = useState(false);
  const isAcceptSubmitted = useRef(false);

  const pendingInvitations = data?.pending_invitations ?? [];
  const invitation: PendingInvitation | undefined =
    openAccountId !== null
      ? pendingInvitations.find(
          ({ account_id }) => account_id === openAccountId,
        )
      : dismissed
      ? undefined
      : pendingInvitations[0];

  const removeFromList = (accountId: number) => {
    queryClient.setQueryData<GetConnectedAccountsResponse | undefined>(
      queryKey.connectedAccounts(),
      (previous) =>
        previous && {
          ...previous,
          pending_invitations: previous.pending_invitations.filter(
            ({ account_id }) => account_id !== accountId,
          ),
        },
    );
    usePendingInvitationStore.setState({ openAccountId: null });
    setIsDeclineConfirmOpen(false);
  };

  const dropDecidedInvitation = (accountId: number) => {
    removeFromList(accountId);
    queryClient.invalidateQueries(queryKey.connectedAccounts());
  };

  const releaseAcceptGuard = () => {
    isAcceptSubmitted.current = false;
  };

  const handleDecisionError = (error: ApiClientError, accountId: number) => {
    if (getErrorCode(error) === INVITATION_NOT_PENDING) {
      dropDecidedInvitation(accountId);
      return;
    }
    if (getErrorStatus(error) === TOO_MANY_REQUESTS) {
      toast(t('accountSwitcher.invitation.tooManyAttempts'), 'error');
      return;
    }
    toast(error?.userMessage ?? t('somethingWentWrong'), 'error');
  };

  const acceptMutation = useMutation(
    (accountId: number) => AccountSwitcherApi.acceptInvitation(accountId),
    {
      onSuccess: (_, accountId) =>
        switchAccount.mutate(accountId, {
          onSettled: () => {
            dropDecidedInvitation(accountId);
            releaseAcceptGuard();
          },
        }),
      onError: (error: ApiClientError, accountId) => {
        releaseAcceptGuard();
        handleDecisionError(error, accountId);
      },
    },
  );

  const declineMutation = useMutation(
    (accountId: number) => AccountSwitcherApi.declineInvitation(accountId),
    {
      onSuccess: (_, accountId) => dropDecidedInvitation(accountId),
      onError: handleDecisionError,
    },
  );

  const isWorkspaceExpired = isTrialExpired || isSubscriptionExpired;
  const isAutomaticPopup = openAccountId === null;

  if (!invitation || (isWorkspaceExpired && isAutomaticPopup)) {
    return null;
  }

  const workspace = invitation.account_name;
  const inviter = invitation.inviter_name;
  const role = t(`userRoles.${invitation.role}`);
  const invitedAt = dayjs(invitation.invited_at);
  const date = invitedAt.isValid() ? formatDate(invitedAt) : null;
  const isAccepting = acceptMutation.isLoading || switchAccount.isLoading;

  const handleAccept = () => {
    if (isAccepting || isAcceptSubmitted.current) return;
    isAcceptSubmitted.current = true;
    acceptMutation.mutate(invitation.account_id);
  };

  const handleCancelDecline = () => {
    if (declineMutation.isLoading) return;
    setIsDeclineConfirmOpen(false);
  };

  return (
    <>
      <Modal
        open={!isDeclineConfirmOpen}
        onClose={close}
        size="xs"
        dataCy={{
          wrapper: 'account-switcher_invitation-modal',
          closeButton: 'account-switcher_invitation-modal-close',
        }}
      >
        <Modal.Header
          title={t('accountSwitcher.invitation.title', { workspace })}
        />
        <Modal.Content>
          <p className="tw-text-sm tw-text-grayscale-180">
            {inviter
              ? t('accountSwitcher.invitation.description', {
                  inviter,
                  workspace,
                  role,
                })
              : t('accountSwitcher.invitation.descriptionNoInviter', {
                  workspace,
                  role,
                })}
          </p>
          <p className="tw-mt-2 tw-text-sm tw-text-grayscale-140">
            {t('accountSwitcher.invitation.loginNote')}
          </p>
          <dl className="tw-mt-4 tw-flex tw-flex-col tw-gap-2 tw-rounded-2xl tw-bg-grayscale-5 tw-p-4">
            <div className="tw-flex tw-items-baseline tw-justify-between tw-gap-4">
              <dt className="tw-text-sm tw-text-grayscale-90">
                {t('accountSwitcher.invitation.workspace')}
              </dt>
              <dd className="tw-text-sm tw-font-semibold">{workspace}</dd>
            </div>
            <div className="tw-flex tw-items-baseline tw-justify-between tw-gap-4">
              <dt className="tw-text-sm tw-text-grayscale-90">
                {t('accountSwitcher.invitation.role')}
              </dt>
              <dd className="tw-text-sm tw-font-semibold">{role}</dd>
            </div>
          </dl>
          {date && (
            <p className="tw-mt-2 tw-text-sm tw-text-grayscale-90">
              {inviter
                ? t('accountSwitcher.invitation.invitedBy', { inviter, date })
                : t('accountSwitcher.invitation.invitedOn', { date })}
            </p>
          )}
          <p className="tw-mt-4 tw-text-xs tw-text-grayscale-140">
            {t('accountSwitcher.invitation.footnote')}
          </p>
        </Modal.Content>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setIsDeclineConfirmOpen(true)}
            disabled={isAccepting}
            data-cy="account-switcher_invitation-modal-decline"
          >
            {t('accountSwitcher.invitation.decline')}
          </Button>
          <Button
            variant="primary"
            onClick={handleAccept}
            isLoading={isAccepting}
            disabled={isAccepting}
            data-cy="account-switcher_invitation-modal-accept"
          >
            {t('accountSwitcher.invitation.accept')}
          </Button>
        </Modal.Footer>
      </Modal>
      <ConfirmDialog
        isOpen={isDeclineConfirmOpen}
        title={t('accountSwitcher.invitation.declineConfirm.title', {
          workspace,
        })}
        message={
          inviter
            ? t('accountSwitcher.invitation.declineConfirm.message', {
                inviter,
              })
            : t('accountSwitcher.invitation.declineConfirm.messageNoInviter')
        }
        confirmLabel={t('accountSwitcher.invitation.declineConfirm.confirm')}
        cancelLabel={t('cancel')}
        confirmVariant="danger"
        isConfirming={declineMutation.isLoading}
        dataCyPrefix="account-switcher_invitation-decline"
        onConfirm={() => declineMutation.mutate(invitation.account_id)}
        onClose={handleCancelDecline}
      />
    </>
  );
};

export default PendingInvitationModal;
